import React, { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Menu, Moon, Sun, X } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem('theme') === 'dark');

  // Dark mode lives only inside the app shell — add the class while Layout is
  // mounted, remove it on the way out so landing/login stay on their design.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    return () => document.documentElement.classList.remove('dark');
  }, [dark]);

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    localStorage.setItem('theme', next ? 'dark' : 'light');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navigation = [
    { name: 'Dashboard', href: '/app/dashboard', icon: '📊' },
    { name: 'My Profile', href: '/app/profile-setup', icon: '👤' },
    { name: 'Create CV', href: '/app/generate', icon: '📝' },
    { name: 'My CVs', href: '/app/cvs', icon: '📂' },
    { name: 'Browse Jobs', href: '/app/jobs', icon: '🔍' },
    { name: 'Applications', href: '/app/applications', icon: '📋' },
    { name: 'Settings', href: '/app/settings', icon: '⚙️' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-50 bg-white border-b border-gray-200 flex items-center justify-between px-4 h-14">
        <h1 className="text-lg font-bold text-gray-900">Veritas</h1>
        <div className="flex items-center gap-1">
          <button onClick={toggleTheme} className="p-2 text-gray-600" title="Toggle theme">
            {dark ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          <button onClick={() => setMenuOpen(o => !o)} className="p-2 text-gray-600" title="Menu">
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/30" onClick={() => setMenuOpen(false)} />
      )}

      {/* Sidebar — fixed on desktop, slide-in drawer on mobile */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform lg:translate-x-0 ${
        menuOpen ? 'translate-x-0 mt-14 lg:mt-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center px-6 py-4 border-b">
            <h1 className="text-xl font-bold text-gray-900">Veritas</h1>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMenuOpen(false)}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="mr-3">{item.icon}</span>
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User info and logout */}
          <div className="border-t px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                    {user?.full_name.charAt(0).toUpperCase()}
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
                  <p className="text-xs text-gray-500">{user?.subscription_tier}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={toggleTheme}
                  className="p-1.5 text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                  title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {dark ? <Sun size={14} /> : <Moon size={14} />}
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-gray-500 border border-gray-200 rounded-lg hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-colors"
                  title="Log out"
                >
                  <LogOut size={14} />
                  Logout
                </button>
              </div>
            </div>
            
            {/* Credits display */}
            <div className="mt-3 px-3 py-2 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">Credits</span>
                <span className="text-sm font-medium text-gray-900">
                  {user?.subscription_tier === 'free' ? user?.credits_remaining : '∞'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 pt-14 lg:pt-0">
        <main className="py-6">
          <div className="mx-auto max-w-7xl px-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;